import React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { type ProductField } from '../../../type';

interface MultiSelectFieldProps {
  field: ProductField;
  selectedOptions: string[];
  onSelectionChange: (fieldId: number, selectedOptions: string[]) => void;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  field,
  selectedOptions,
  onSelectionChange,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const handleOptionToggle = (optionId: string) => {
    const newSelection = selectedOptions.includes(optionId)
      ? selectedOptions.filter(id => id !== optionId)
      : [...selectedOptions, optionId];
    
    onSelectionChange(field.id, newSelection);
  };

  const getSelectedLabels = () => {
    return field.options
      .filter(option => selectedOptions.includes(option.id))
      .map(option => option.value)
      .join(', ');
  };

  // ⬇️ Handle outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {field.label}
      </label>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-left focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition-colors"
        >
          <div className="flex items-center justify-between">
            <span className="text-gray-700 truncate">
              {selectedOptions.length === 0 
                ? `Select ${field.label.toLowerCase()}...` 
                : getSelectedLabels()
              }
            </span>
            <ChevronDown 
              className={`w-5 h-5 text-gray-400 transition-transform ${
                isOpen ? 'rotate-180' : ''
              }`}
            />
          </div>
          {selectedOptions.length > 0 && (
            <div className="mt-1">
              <span className="text-sm text-black font-medium">
                {selectedOptions.length} selected
              </span>
            </div>
          )}
        </button>

        {isOpen && (
          <div className="absolute z-10 left-0 min-w-[320px] max-w-[400px] w-auto mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            <div className="p-2">
              {field.options.map((option) => (
                <label
                  key={option.id}
                  className="flex items-start p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedOptions.includes(option.id)}
                    onChange={() => handleOptionToggle(option.id)}
                    className="sr-only"
                  />
                  <div className={`flex items-center justify-center w-5 h-5 flex-shrink-0 mt-0.5 border-2 rounded transition-colors ${
                    selectedOptions.includes(option.id)
                      ? 'bg-black border-black'
                      : 'border-gray-300'
                  }`}>
                    {selectedOptions.includes(option.id) && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className="ml-3 text-sm text-gray-700 break-words min-w-0">
                    {option.value}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiSelectField;
